import React, { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  ButtonContainer,
  ContentSection,
  Fieldset,
  Heading,
  Layout,
  PageWrap,
  SectionHeading,
} from '@vwfs-bronson/bronson-react';
import { Page } from '../../../..';
import { PaginationComponent } from '../../../../components/Pagination';
import { DealerDashboardSubpage, QuoteFlow } from '../../../../resources/base/models/enums';
import { routes } from '../../../../routing/routes';
import {
  cancelPendingContract,
  getPendingContracts,
  getQuote,
  updateSalesPersonPendingContract,
} from '../../../../services/api/miles/miles-services';
import { MilesPendingContractsListResponse } from '../../../../services/api/miles/types';
import { assertDefined, isNotesEnabled } from '../../../../services/common/miscUtils';
import { staticSelector, store } from '../../../../services/redux';
import { brokerSelectors } from '../../../../services/redux/features/broker.slice';
import { setIsCarUsed, setSelectedVehicleType } from '../../../../services/redux/features/custom.slice';
import { setCustomerInitialState } from '../../../../services/redux/features/customer.slice';
import {
  clearSelectedComparisonOfferIds,
  dashboardSelectors,
  resetDashboardSearchForm,
  setContractsNumbersAcceptedPage,
  setDashboardSearchFormWithSelections,
  setSubPageLabelStatusIds,
} from '../../../../services/redux/features/dashboard.slice';
import { loadingActions } from '../../../../services/redux/features/loading.slice';
import { setQuoteData, setQuoteFlow } from '../../../../services/redux/features/quote.slice';
import { getInitialCustomerStateFromQuote } from '../../../../services/redux/reduxUtils';
import { useNavigate } from '../../../../services/routing/navigate';
import { getVehicleType, handleCopyQuote, isCarUsedFromQuote, scrollToTop } from '../../../utils';
import { ButtonAction, Buttons } from '../../constants/ButtonAction';
import { paginationInitialState } from '../../constants/DefaultPagination';
import {
  filteringStatusIdsAcceptedSubpage,
  offerStatusIdOptionsList,
  OfferStatusIdType,
} from '../../constants/StatusMap';
import { DashboardActionPopup, DashboardPopupTransfer } from '../../popups/DashboardPopups';
import { DashBoardSearchSection } from '../components/DashBoardSearchSection';
import { DashboardSubpagesTabs } from '../components/DashboardSubpagesTabs';
import { DashBoardTable } from '../components/DashBoardTable';
import { useDashboardNotesUtils } from '../utils/dashboardNotesUtils';
import { handleSelectionAfterTableUpdate } from '../utils/dashboardSubpageTableSelection';
import { initializeSalesPersonDropdown } from '../utils/initializeSalesPersonDropdown';
import { mapResponsePagination } from '../utils/mapResponsePagination';
import { SearchedValues } from '../utils/searchedValues.types';

const tableHeaders = [
  'reference',
  'statusId',
  ...(isNotesEnabled() ? ['notes'] : []),
  'tradingName',
  'siren',
  'usage',
  'manufacturer',
  'modelDescription',
  'productName',
  'durationMileage',
];

const buttonList = [
  { value: 'Copier', label: 'Copier', enableOnMultiSelect: false },
  { value: 'Annuler', label: 'Annuler', enableOnMultiSelect: false },
  { value: 'Transfert', label: 'Transfert', enableOnMultiSelect: true },
  { value: 'Paiement', label: 'Paiement', enabledOnStatuses: ['100215', '100217'], enableOnMultiSelect: false },
  { value: 'Documents', label: 'Documents', enableOnMultiSelect: false },
] as Buttons;

export const DashboardSubpageAccepted: FunctionComponent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { getNotesDashboardButtonFromConversations } = useDashboardNotesUtils();

  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [showCopyPopup, setShowCopyPopup] = useState(false);
  const [showModifyPopup, setShowModifyPopup] = useState(false);
  const [showCancelPopup, setShowCancelPopup] = useState(false);
  const [showTransfertPopup, setShowTransfertPopup] = useState(false);
  const [pendingContracts, setPendingContracts] = useState<any[]>([]);
  const [pagination, setPagination] = useState(paginationInitialState);
  const [contractsTotalCount, setContractsTotalCount] = useState<number>(0);
  const [searchedValues, setSearchedValues] = useState<SearchedValues>({});
  const [salesPersons, setSalesPersons] = useState<Array<{ value: string; label: string }>>([] as any);
  const [chosenSalesPerson, setChosenSalesPerson] = useState<string>('');

  const milesSalesPersonId = useSelector(brokerSelectors.milesSalesPersonId);
  const subPageLabelStatusIds: OfferStatusIdType[] | undefined = useSelector(
    dashboardSelectors.selectSubPageLabelStatusIds
  );
  const overallFilterStatusIds: OfferStatusIdType[] = subPageLabelStatusIds || filteringStatusIdsAcceptedSubpage;

  const searchForm = useSelector(dashboardSelectors.selectDashboardSearchFormWithSelections);
  const hasStoredSearch = !!searchForm && Object.keys(searchForm).length > 0;

  const tableRef = useRef<any>(null);

  const resetTableSelections = () => {
    if (tableRef.current) {
      tableRef.current.clearSelections(); // Call the reset method on the child
    }
  };

  const mapPendingContractsToRows = ({ financialProduct }: MilesPendingContractsListResponse): any[] => {
    return (
      financialProduct?.pendingContracts?.map((contract) => {
        const { applicants, reference, offerId, vehicleDescription, statusId, contractId, product, conversations } =
          contract;
        const [manufacturer, ...rest] = (vehicleDescription || '').split(' ');
        const modelDescription = rest.join(' ');
        const firstApplicant = applicants?.[0];
        const isPrivate = firstApplicant?.customerType === 'INDIVIDUAL';
        const notes = getNotesDashboardButtonFromConversations('PENDING_CONTRACT', reference, conversations);

        return {
          contractId,
          offerId,
          reference,
          tradingName: firstApplicant?.tradingName,
          siren: firstApplicant?.companyData?.companyRegistrationNumber,
          usage: isPrivate
            ? t('dashboard:table:body:privateCustomerUsage')
            : t('dashboard:table:body:businessCustomerUsage'),
          manufacturer,
          modelDescription,
          productName: product?.productAnnotation,
          durationMileage: `${product?.duration || ''}/${product?.contractMileage || ''}`,
          statusId: offerStatusIdOptionsList[statusId as OfferStatusIdType]?.label,
          notes,
        };
      }) || []
    );
  };

  const selectedContracts = useMemo(
    () => pendingContracts.filter(({ offerId }) => selectedItems.includes(offerId)),
    [selectedItems, pendingContracts]
  );

  const selectedContractIds = selectedContracts.map((contract) => contract.contractId);
  const selectedReferences = selectedContracts.map((contract) => contract.reference);

  const doGetPendingContracts = async (data: any = {}) => {
    const currentCompany = staticSelector(brokerSelectors.currentCompany);
    const milesCompanyIds = [assertDefined(currentCompany?.milesCompanyId)];

    try {
      store.dispatch(loadingActions.setLoading(true));
      const requestData = data?.statusId
        ? data
        : {
            ...data,
            statusId: overallFilterStatusIds,
          };
      const pendingContractsResponse = await getPendingContracts({
        milesCompanyIds,
        ...(searchForm.currentPage ? { page: searchForm.currentPage } : {}),
        ...requestData,
      });
      setContractsTotalCount(pendingContractsResponse?.totalCount);

      setPagination(mapResponsePagination(pendingContractsResponse));

      if (pendingContractsResponse?.financialProduct?.pendingContracts?.length > 0) {
        const contractRows = mapPendingContractsToRows(pendingContractsResponse);

        setPendingContracts(contractRows);
        setSelectedItems((prev) => handleSelectionAfterTableUpdate(prev, contractRows, resetTableSelections));
      } else {
        resetTableSelections();
        setPendingContracts([]);
      }

      store.dispatch(loadingActions.setLoading(false));
    } catch (error) {
      store.dispatch(loadingActions.setLoading(false));
      navigate.error();
    }
  };

  const cancelRow = async () => {
    try {
      store.dispatch(loadingActions.setLoading(true));
      await cancelPendingContract(selectedContractIds[0]);
      await doGetPendingContracts();
      store.dispatch(loadingActions.setLoading(false));
    } catch {
      store.dispatch(loadingActions.setLoading(false));
      navigate.error();
    }
  };

  const modifyRow = async () => {
    try {
      store.dispatch(loadingActions.setLoading(true));
      const quoteData = await getQuote(selectedItems[0]);
      store.dispatch(setIsCarUsed(isCarUsedFromQuote(quoteData)));
      const customerInitialState = await getInitialCustomerStateFromQuote(quoteData);
      store.dispatch(setCustomerInitialState(customerInitialState));
      store.dispatch(setSelectedVehicleType(getVehicleType(quoteData)));
      store.dispatch(setQuoteData(quoteData));
      store.dispatch(setQuoteFlow(QuoteFlow.MODIFY));
      store.dispatch(loadingActions.setLoading(false));
      navigate.form(`/3`);
    } catch {
      store.dispatch(loadingActions.setLoading(false));
      navigate.error();
    }
  };

  const copySelectedQuote = async () => {
    await handleCopyQuote(selectedItems[0], navigate, 3);
  };

  const handleButtonClick = (action: ButtonAction) => {
    const dispatchSavedState = {
      ...searchedValues,
      selectedCheckboxes: selectedItems,
      currentPage: pagination?.currentPage,
    };
    switch (action) {
      case 'Copier':
        setShowCopyPopup(true);
        break;
      case 'Annuler':
        setShowCancelPopup(true);
        break;
      case 'Transfert':
        setShowTransfertPopup(true);
        break;
      case 'Paiement':
        dispatch(setDashboardSearchFormWithSelections(dispatchSavedState));
        navigate.dashboardPayment(`?contractId=${selectedContractIds}&reference=${selectedReferences}`);
        scrollToTop();
        break;
      case 'Documents':
        dispatch(setDashboardSearchFormWithSelections(dispatchSavedState));
        navigate.url(
          `${routes.dashboardAcceptedDocuments.replace(':id', selectedItems[0])}?contractId=${selectedContractIds}&contractStatus=${selectedContracts[0].statusId}`
        );
        break;
      default:
        break;
    }
  };

  const handleConfirm = async (action: ButtonAction) => {
    switch (action) {
      case 'Copier':
        setShowCopyPopup(false);
        copySelectedQuote();
        break;
      case 'Modifier':
        setShowModifyPopup(false);
        modifyRow();
        break;
      case 'Annuler':
        setShowCancelPopup(false);
        cancelRow();
        break;
      case 'Transfert':
        store.dispatch(loadingActions.setLoading(true));
        try {
          await Promise.all(
            selectedContractIds.map((contractId) => updateSalesPersonPendingContract(chosenSalesPerson, contractId))
          );
        } catch (err) {
          navigate.error();
        } finally {
          setSelectedItems([]);
          resetTableSelections();
          setShowTransfertPopup(false);
          store.dispatch(loadingActions.setLoading(false));
        }
        break;
      default:
        break;
    }
  };

  const paginationOnChange = async (page: Page) => {
    await doGetPendingContracts({ page, ...searchedValues });
    setSelectedItems([]);
    resetTableSelections();
  };

  const isButtonDisabled = (
    enabledOnStatuses: OfferStatusIdType[] | undefined,
    enableOnMultiSelect: boolean | undefined
  ): boolean => {
    if (!selectedItems?.length) {
      return true;
    }
    if (!enableOnMultiSelect && selectedItems?.length !== 1) {
      return true;
    }

    if (enabledOnStatuses) {
      const { statusId } = pendingContracts.find((item) => selectedItems.includes(item.offerId)) || {};
      const entries = Object.entries(offerStatusIdOptionsList)
        .filter(([, entry]) => entry.label === statusId)
        .map(([id]) => id);
      return !enabledOnStatuses.some((status) => entries.includes(status));
    }

    return false;
  };

  const onTableSelection = (selectedIds: string[]) => {
    dispatch(setContractsNumbersAcceptedPage(selectedReferences));
    setSelectedItems(selectedIds);
  };

  const searchFieldsMap = (values: Partial<SearchedValues>): SearchedValues => ({
    ...(values?.searchQuery && { searchQuery: values?.searchQuery }),
    ...(values?.salesPerson && { salesPerson: values?.salesPerson }),
    ...(values?.statusId && { statusId: values?.statusId }),
    pageSize: pagination?.pageSize,
  });

  const searchQuotes = (values: Partial<SearchedValues>) => {
    dispatch(setSubPageLabelStatusIds(undefined));
    const newSearchedValues: SearchedValues = searchFieldsMap(values);
    setSearchedValues(newSearchedValues);
    doGetPendingContracts({
      ...newSearchedValues,
      statusId: newSearchedValues.statusId ?? filteringStatusIdsAcceptedSubpage,
    });
  };

  const onRowDbClick = (selectedId: string) => {
    setSelectedItems([selectedId]);
    setShowModifyPopup(true);
  };

  useEffect(() => {
    dispatch(clearSelectedComparisonOfferIds());
    const fetchData = async () => {
      if (hasStoredSearch) {
        dispatch(resetDashboardSearchForm());
        searchQuotes(searchForm);
      } else if (milesSalesPersonId) {
        const defaultFilter = { salesPerson: milesSalesPersonId };
        setSearchedValues(defaultFilter);
        await doGetPendingContracts(defaultFilter);
      } else {
        await doGetPendingContracts();
      }

      await initializeSalesPersonDropdown(setSalesPersons);
    };

    fetchData();
    return () => {
      dispatch(setSubPageLabelStatusIds(undefined));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <ContentSection pageWrap className="u-pt-none">
        <DashboardSubpagesTabs />
      </ContentSection>

      <ContentSection className="u-pv-small" pageWrap>
        <Layout>
          <Layout.Item default="1/1" s="1/1" className="u-mt-none">
            <Heading level={2}>{t('dashboard:search:header')}</Heading>
          </Layout.Item>
        </Layout>
      </ContentSection>

      <PageWrap>
        <Layout>
          <Layout.Item default="1/1" s="1/1" className="u-mt-none">
            <DashBoardSearchSection
              searchQuotes={searchQuotes}
              subpage={DealerDashboardSubpage.Accepted}
              fields={['salesPerson', 'statusId']}
              salesPersonDropdown={salesPersons}
              searchQuery
              defaultSalesPerson={milesSalesPersonId}
            />
          </Layout.Item>
        </Layout>
      </PageWrap>

      <ContentSection pageWrap>
        <Layout>
          <Layout.Item className="u-text-center u-mt-none">
            <SectionHeading level={3} className="u-text-left">
              {t('dashboard:search:results', {
                numResults: contractsTotalCount,
              })}
            </SectionHeading>
            <DashBoardTable
              ref={tableRef}
              headers={tableHeaders}
              onSelection={onTableSelection}
              values={pendingContracts}
              onDoubleClick={onRowDbClick}
            />
            <Layout center>
              <PaginationComponent pagination={pagination} onChange={paginationOnChange} />
            </Layout>
          </Layout.Item>
        </Layout>
      </ContentSection>

      <Fieldset.Row>
        <ButtonContainer center>
          <Layout.Item className="u-text-center">
            {buttonList?.map(({ label, value, enabledOnStatuses, enableOnMultiSelect }) => (
              <Button
                key={`${label}`}
                value={`${value}`}
                type="button"
                secondary
                className="u-m-small"
                onClick={() => handleButtonClick(label)}
                disabled={isButtonDisabled(enabledOnStatuses, enableOnMultiSelect)}
              >
                {label}
              </Button>
            ))}
            {showCopyPopup && (
              <DashboardActionPopup
                handleClose={() => setShowCopyPopup(false)}
                onConfirm={() => handleConfirm('Copier')}
                text={t('dashboard:popup:copyTextTitle')}
              />
            )}

            {showModifyPopup && (
              <DashboardActionPopup
                handleClose={() => setShowModifyPopup(false)}
                onConfirm={() => handleConfirm('Modifier')}
                text={t('dashboard:popup:modifyReadOnlyQuotePopup')}
              />
            )}

            {showCancelPopup && (
              <DashboardActionPopup
                handleClose={() => setShowCancelPopup(false)}
                onConfirm={() => handleConfirm('Annuler')}
                text={t('dashboard:popup:cancelTextTitle')}
              />
            )}

            {showTransfertPopup && (
              <DashboardPopupTransfer
                handleClose={() => setShowTransfertPopup(false)}
                onConfirm={() => handleConfirm('Transfert')}
                salesPersons={salesPersons}
                chooseSalesPerson={setChosenSalesPerson}
                quoteIds={selectedContractIds}
                subpage={DealerDashboardSubpage.Accepted}
              />
            )}
          </Layout.Item>
        </ButtonContainer>
      </Fieldset.Row>
    </>
  );
};
